(function () {
  "use strict";

  var GLOBAL_KEY = "BotanicalisPredictiveSearch";
  var ROOT_SELECTOR = "[data-bot-predictive-search]";
  var MIN_QUERY_LENGTH = 2;
  var MAX_RESULTS = 6;
  var DEBOUNCE_DELAY = 250;

  if (window[GLOBAL_KEY]) {
    window[GLOBAL_KEY].init(document);
    return;
  }

  function debounce(callback, delay) {
    var timer = null;

    function debounced() {
      var context = this;
      var args = arguments;
      window.clearTimeout(timer);
      timer = window.setTimeout(function () {
        timer = null;
        callback.apply(context, args);
      }, delay);
    }

    debounced.cancel = function () {
      window.clearTimeout(timer);
      timer = null;
    };

    return debounced;
  }

  function normalizedQuery(value) {
    return String(value || "").replace(/\s+/g, " ").trim();
  }

  function safeUrl(value, fallback, sameOriginOnly) {
    if (!value) return fallback || "";
    try {
      var url = new URL(value, window.location.origin);
      if (url.protocol !== "http:" && url.protocol !== "https:") return fallback || "";
      if (sameOriginOnly && url.origin !== window.location.origin) return fallback || "";
      return url.href;
    } catch (error) {
      return fallback || "";
    }
  }

  function PredictiveSearch(root) {
    this.root = root;
    this.form = root.querySelector("[data-bot-predictive-form]");
    this.input = root.querySelector("[data-bot-predictive-input]");
    this.results = root.querySelector("[data-bot-predictive-results]");
    this.status = root.querySelector("[data-bot-predictive-status]");
    this.heading = root.querySelector("[data-bot-predictive-heading]");
    this.list = root.querySelector("[data-bot-predictive-list]");
    this.allResults = root.querySelector("[data-bot-predictive-all]");
    this.endpoint = root.dataset.predictiveSearchUrl || "";
    this.searchUrl = root.dataset.searchUrl || (this.form ? this.form.action : "");
    this.locale = root.dataset.searchLocale || document.documentElement.lang || "sk-SK";
    this.currency = root.dataset.searchCurrency || "EUR";
    this.abortController = null;
    this.activeIndex = -1;
    this.optionElements = [];
    this.lastQuery = "";
    this.requestVersion = 0;

    if (!this.form || !this.input || !this.results || !this.status || !this.heading || !this.list || !this.allResults || !this.endpoint) {
      return;
    }

    this.onInput = this.handleInput.bind(this);
    this.onKeydown = this.handleKeydown.bind(this);
    this.onFocus = this.handleFocus.bind(this);
    this.onFocusOut = this.handleFocusOut.bind(this);
    this.onSubmit = this.handleSubmit.bind(this);
    this.debouncedFetch = debounce(this.fetchResults.bind(this), DEBOUNCE_DELAY);

    this.input.addEventListener("input", this.onInput);
    this.input.addEventListener("keydown", this.onKeydown);
    this.input.addEventListener("focus", this.onFocus);
    this.root.addEventListener("focusout", this.onFocusOut);
    this.form.addEventListener("submit", this.onSubmit);

    this.root.dataset.botPredictiveInitialized = "true";
    this.root.botanicalisPredictiveSearch = this;
    this.results.setAttribute("aria-busy", "false");
  }

  PredictiveSearch.prototype.abortPendingRequest = function () {
    if (this.abortController) this.abortController.abort();
    this.abortController = null;
  };

  PredictiveSearch.prototype.handleInput = function () {
    var query = normalizedQuery(this.input.value);
    this.requestVersion += 1;
    this.debouncedFetch.cancel();
    this.abortPendingRequest();
    this.clearActiveOption();

    if (query.length < MIN_QUERY_LENGTH) {
      this.lastQuery = "";
      this.close();
      return;
    }

    this.debouncedFetch(query, this.requestVersion);
  };

  PredictiveSearch.prototype.handleFocus = function () {
    var query = normalizedQuery(this.input.value);
    if (query.length >= MIN_QUERY_LENGTH && query === this.lastQuery && (this.optionElements.length || this.status.textContent)) {
      this.open();
    }
  };

  PredictiveSearch.prototype.handleFocusOut = function () {
    var instance = this;
    window.setTimeout(function () {
      if (!instance.root.contains(document.activeElement)) instance.close();
    }, 0);
  };

  function arkswiftCollectionUrl(searchUrl) {
    var url = new URL(safeUrl(searchUrl, window.location.origin + "/search", true), window.location.origin);
    if (!/\/search\/?$/.test(url.pathname)) return null;
    url.pathname = url.pathname.replace(/\/search\/?$/, "/collections/arkswift");
    url.search = "";
    url.hash = "";
    return url.href;
  }

  function isArkswiftQuery(query) {
    var value = normalizedQuery(query).toLowerCase();
    return value === "arkswift" || value === "vendor:arkswift";
  }

  PredictiveSearch.prototype.handleSubmit = function (event) {
    this.input.value = normalizedQuery(this.input.value);
    if (isArkswiftQuery(this.input.value)) {
      var target = arkswiftCollectionUrl(this.searchUrl);
      if (target) {
        event.preventDefault();
        window.location.assign(target);
      }
    }
  };

  PredictiveSearch.prototype.handleKeydown = function (event) {
    if (event.key === "Escape" && !this.results.hidden) {
      event.preventDefault();
      event.stopPropagation();
      this.close();
      return;
    }

    if (!this.optionElements.length || this.results.hidden) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      this.setActiveOption(this.activeIndex + 1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      this.setActiveOption(this.activeIndex - 1);
    } else if (event.key === "Home" && this.activeIndex >= 0) {
      event.preventDefault();
      this.setActiveOption(0);
    } else if (event.key === "End" && this.activeIndex >= 0) {
      event.preventDefault();
      this.setActiveOption(this.optionElements.length - 1);
    } else if (event.key === "Enter" && this.activeIndex >= 0) {
      var link = this.optionElements[this.activeIndex];
      if (link) {
        event.preventDefault();
        window.location.assign(link.href);
      }
    }
  };

  PredictiveSearch.prototype.setActiveOption = function (index) {
    if (!this.optionElements.length) return;

    if (index < 0) index = this.optionElements.length - 1;
    if (index >= this.optionElements.length) index = 0;

    this.optionElements.forEach(function (option, optionIndex) {
      option.setAttribute("aria-selected", optionIndex === index ? "true" : "false");
    });

    this.activeIndex = index;
    this.input.setAttribute("aria-activedescendant", this.optionElements[index].id);
    this.optionElements[index].scrollIntoView({ block: "nearest" });
  };

  PredictiveSearch.prototype.clearActiveOption = function () {
    this.optionElements.forEach(function (option) {
      option.setAttribute("aria-selected", "false");
    });
    this.activeIndex = -1;
    this.input.removeAttribute("aria-activedescendant");
  };

  PredictiveSearch.prototype.clearProducts = function () {
    this.list.replaceChildren();
    this.optionElements = [];
    this.clearActiveOption();
    this.heading.hidden = true;
  };

  PredictiveSearch.prototype.updateAllResultsLink = function (query) {
    var fallback = safeUrl(this.searchUrl, window.location.origin + "/search", true);
    var url;

    try {
      url = new URL(fallback, window.location.origin);
      var arkswiftTarget = isArkswiftQuery(query) ? arkswiftCollectionUrl(fallback) : null;
      if (arkswiftTarget) {
        url = new URL(arkswiftTarget);
      } else {
        url.searchParams.set("q", query);
        url.searchParams.set("type", "product");
        url.searchParams.set("options[prefix]", "last");
        url.searchParams.set("options[unavailable_products]", "hide");
      }
      this.allResults.href = url.href;
      this.allResults.textContent = "Zobraziť všetky výsledky pre „" + query + "“";
      this.allResults.hidden = false;
    } catch (error) {
      this.allResults.hidden = true;
    }
  };

  PredictiveSearch.prototype.showStatus = function (message, query, loading) {
    this.clearProducts();
    this.status.textContent = message;
    this.status.classList.toggle("is-loading", Boolean(loading));
    this.results.setAttribute("aria-busy", loading ? "true" : "false");
    this.updateAllResultsLink(query);
    this.open();
  };

  PredictiveSearch.prototype.open = function () {
    this.results.hidden = false;
    this.input.setAttribute("aria-expanded", "true");
  };

  PredictiveSearch.prototype.close = function () {
    this.results.hidden = true;
    this.results.setAttribute("aria-busy", "false");
    this.input.setAttribute("aria-expanded", "false");
    this.clearActiveOption();
  };

  PredictiveSearch.prototype.productPrice = function (product) {
    var minimum = Number(product.price_min != null ? product.price_min : product.price);
    var maximum = Number(product.price_max != null ? product.price_max : minimum);
    if (!Number.isFinite(minimum)) return "";

    try {
      var formatted = new Intl.NumberFormat(this.locale, {
        style: "currency",
        currency: this.currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(minimum);
      return Number.isFinite(maximum) && maximum > minimum ? "Od " + formatted : formatted;
    } catch (error) {
      return minimum.toFixed(2).replace(".", ",") + " " + this.currency;
    }
  };

  PredictiveSearch.prototype.productTypeLabel = function (value, title) {
    var labels = {
      "Plant": "Živá rastlina",
      "Live Plant": "Živá rastlina",
      "Artificial Plant": "Umelá rastlina",
      "Artificial Tree": "Umelý strom",
      "Hanging Plant": "Visiaca rastlina",
      "Green Wall Panel": "Zelený stenový panel",
      "Branch & Stem": "Vetva a stonka",
      "Pot": "Kvetináč",
      "Planter": "Kvetináč",
      "Bouquet": "Kytica",
      "Artificial Bouquet": "Kytica",
      "Garland": "Girlanda",
      "Artificial Garland": "Girlanda",
      "Hedge": "Umelý živý plot",
      "Artificial Hedge": "Umelý živý plot",
      "Leaf": "Dekoratívny list",
      "Artificial Leaf": "Dekoratívny list",
      "Standard": "Produkt",
      "Náhradní díly pro plynové grily": "Náhradné diely pre plynové grily",
      "Příslušenství": "Príslušenstvo",
      "Popolnice": "Odpadkové nádoby",
      "Rudlíky": "Manipulačné vozíky",
      "Záhradný fúriky": "Záhradné fúriky",
      "Mulčovacia textílie": "Mulčovacie textílie",
      "Tieniace sieť": "Tieniace siete",
      "Drviče vetví": "Drviče konárov",
      "Benzinové píly": "Benzínové píly",
      "Trávne zmesy": "Trávne zmesi"
    };
    var base = value === "Plant" && /artificial|kunstplant|kunstbloem/i.test(String(title || ""))
      ? "Umelá rastlina"
      : (labels[value] || value || "Produkt");
    var language = String(document.documentElement.lang || "sk").toLowerCase().split("-")[0];
    var localized = {
      de: { "Živá rastlina": "Lebende Pflanze", "Umelá rastlina": "Kunstpflanze", "Umelý strom": "Kunstbaum", "Visiaca rastlina": "Hängepflanze", "Zelený stenový panel": "Grünwandpaneel", "Vetva a stonka": "Zweig und Stiel", "Kvetináč": "Pflanzgefäß", "Kytica": "Blumenstrauß", "Girlanda": "Girlande", "Umelý živý plot": "Kunsthecke", "Dekoratívny list": "Dekoratives Blatt", "Produkt": "Produkt", "Príslušenstvo": "Zubehör" },
      fr: { "Živá rastlina": "Plante naturelle", "Umelá rastlina": "Plante artificielle", "Umelý strom": "Arbre artificiel", "Visiaca rastlina": "Plante retombante", "Zelený stenový panel": "Panneau végétal", "Vetva a stonka": "Branche et tige", "Kvetináč": "Pot de fleurs", "Kytica": "Bouquet", "Girlanda": "Guirlande", "Umelý živý plot": "Haie artificielle", "Dekoratívny list": "Feuille décorative", "Produkt": "Produit", "Príslušenstvo": "Accessoires" }
    };
    return localized[language] && localized[language][base] ? localized[language][base] : base;
  };

  PredictiveSearch.prototype.createProductOption = function (product, index) {
    var fallbackUrl = this.allResults.href || this.searchUrl;
    var productUrl = safeUrl(product.url, fallbackUrl, true);
    var imageValue = product.featured_image && product.featured_image.url ? product.featured_image.url : product.image;
    var imageUrl = safeUrl(imageValue, "", false);
    var option = document.createElement("li");
    var link = document.createElement("a");
    var media = document.createElement("span");
    var copy = document.createElement("span");
    var type = document.createElement("small");
    var title = document.createElement("strong");
    var price = document.createElement("em");
    var instance = this;

    option.className = "botanicalis-predictive__option";
    option.setAttribute("role", "none");

    link.className = "botanicalis-predictive__link";
    link.id = this.list.id + "-option-" + index;
    link.href = productUrl;
    link.tabIndex = -1;
    link.setAttribute("role", "option");
    link.setAttribute("aria-selected", "false");

    media.className = "botanicalis-predictive__media";
    if (imageUrl) {
      var image = document.createElement("img");
      image.src = imageUrl;
      image.alt = "";
      image.width = 136;
      image.height = 136;
      image.loading = "lazy";
      image.decoding = "async";
      media.append(image);
    }

    copy.className = "botanicalis-predictive__copy";
    type.className = "botanicalis-predictive__type";
    type.textContent = this.productTypeLabel(product.type, product.title);
    title.className = "botanicalis-predictive__title";
    title.textContent = product.title || "Produkt";
    copy.append(type, title);

    price.className = "botanicalis-predictive__price";
    price.textContent = this.productPrice(product);
    link.append(media, copy);
    if (price.textContent) link.append(price);
    option.append(link);

    link.addEventListener("pointerenter", function () {
      instance.setActiveOption(index);
    });

    return option;
  };

  PredictiveSearch.prototype.renderProducts = function (products, query) {
    var instance = this;
    this.clearProducts();
    this.status.textContent = "";
    this.status.classList.remove("is-loading");
    this.results.setAttribute("aria-busy", "false");

    if (!products.length) {
      this.showStatus("Nenašli sme žiadne dostupné produkty. Skúste iný výraz alebo zobrazte celé vyhľadávanie.", query, false);
      return;
    }

    products.slice(0, MAX_RESULTS).forEach(function (product, index) {
      var option = instance.createProductOption(product, index);
      instance.list.append(option);
      instance.optionElements.push(option.querySelector('[role="option"]'));
    });

    this.heading.hidden = false;
    this.updateAllResultsLink(query);
    this.open();
  };

  PredictiveSearch.prototype.fetchResults = function (query, requestVersion) {
    var instance = this;
    var endpoint;

    try {
      endpoint = new URL(this.endpoint, window.location.origin);
      endpoint.searchParams.set("q", query);
      endpoint.searchParams.set("resources[type]", "product");
      endpoint.searchParams.set("resources[limit]", String(MAX_RESULTS));
      endpoint.searchParams.set("resources[limit_scope]", "all");
      endpoint.searchParams.set("resources[options][unavailable_products]", "hide");
    } catch (error) {
      this.showStatus("Návrhy sa momentálne nepodarilo načítať. Celý katalóg otvoríte tlačidlom vyššie.", query, false);
      return;
    }

    this.showStatus("Hľadám v celom katalógu…", query, true);
    this.abortPendingRequest();
    this.abortController = typeof AbortController === "function" ? new AbortController() : null;
    var currentController = this.abortController;
    var fetchOptions = {
      headers: { Accept: "application/json" },
      credentials: "same-origin"
    };
    if (currentController) fetchOptions.signal = currentController.signal;

    fetch(endpoint.href, fetchOptions)
      .then(function (response) {
        if (!response.ok) throw new Error("Predictive search request failed: " + response.status);
        return response.json();
      })
      .then(function (payload) {
        if (requestVersion !== instance.requestVersion || query !== normalizedQuery(instance.input.value)) return;
        var resources = payload && payload.resources && payload.resources.results;
        var products = resources && Array.isArray(resources.products) ? resources.products : [];
        products = products.filter(function (product) { return product && product.available !== false; });
        instance.lastQuery = query;
        instance.renderProducts(products, query);
      })
      .catch(function (error) {
        if (error && error.name === "AbortError") return;
        if (requestVersion !== instance.requestVersion) return;
        instance.lastQuery = query;
        instance.showStatus("Návrhy sa momentálne nepodarilo načítať. Stlačením Enter zobrazíte celé vyhľadávanie.", query, false);
      })
      .finally(function () {
        if (instance.abortController === currentController) instance.abortController = null;
      });
  };

  PredictiveSearch.prototype.destroy = function () {
    if (!this.input || !this.form) return;
    this.debouncedFetch.cancel();
    this.abortPendingRequest();
    this.input.removeEventListener("input", this.onInput);
    this.input.removeEventListener("keydown", this.onKeydown);
    this.input.removeEventListener("focus", this.onFocus);
    this.root.removeEventListener("focusout", this.onFocusOut);
    this.form.removeEventListener("submit", this.onSubmit);
    delete this.root.dataset.botPredictiveInitialized;
    this.root.botanicalisPredictiveSearch = null;
  };

  function rootsWithin(scope) {
    var roots = [];
    if (!scope) return roots;
    if (scope.matches && scope.matches(ROOT_SELECTOR)) roots.push(scope);
    if (scope.querySelectorAll) roots = roots.concat(Array.from(scope.querySelectorAll(ROOT_SELECTOR)));
    return roots;
  }

  function init(scope) {
    rootsWithin(scope || document).forEach(function (root) {
      if (!root.botanicalisPredictiveSearch) new PredictiveSearch(root);
    });
  }

  function destroy(scope) {
    rootsWithin(scope).forEach(function (root) {
      if (root.botanicalisPredictiveSearch) root.botanicalisPredictiveSearch.destroy();
    });
  }

  window[GLOBAL_KEY] = { init: init, destroy: destroy };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { init(document); }, { once: true });
  } else {
    init(document);
  }

  document.addEventListener("shopify:section:load", function (event) { init(event.target); });
  document.addEventListener("shopify:section:unload", function (event) { destroy(event.target); });
})();

